import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';

import StyledLoading from '/shared/styled/StyledLoading';

interface Props {
  $dark?: boolean;
  $hasNoOffset?: boolean;
  isWrapped?: boolean;
  $isFixed?: boolean;
  $isRelative?: boolean;
  $isSmall?: boolean;
  delay?: number;
  fallback?: {
    timeoutMS?: number;
    component?: ReactNode | null;
  };
}

function Loading({
  $dark = false,
  isWrapped = false,
  $isFixed = false,
  $isRelative = false,
  $isSmall = false,
  $hasNoOffset = false,
  delay = 50,
  fallback = { timeoutMS: 5000, component: null },
}: Props) {
  const hasDelay = delay <= 0;
  const [longLoad, setLongLoad] = useState(hasDelay);
  const [displayFallbackComponent, setDisplayFallbackComponent] = useState(false);

  useEffect(() => {
    if (hasDelay) {
      return;
    }

    const shortLoadTimeout = setTimeout(() => setLongLoad(true), delay);

    const fallbackTimeout = setTimeout(() => {
      if (fallback?.component) {
        setDisplayFallbackComponent(true);
      }
    }, fallback?.timeoutMS);

    return () => {
      clearTimeout(shortLoadTimeout);
      clearTimeout(fallbackTimeout);
    };
  }, [delay, fallback?.component, fallback?.timeoutMS, hasDelay]);

  if (!longLoad) {
    // Show spinner only if load exceeds Xms (defined in shortLoad timeout)
    return null;
  }
  if (displayFallbackComponent && fallback?.component) {
    return fallback?.component;
  }
  if (isWrapped) {
    return (
      <div style={{ minHeight: '128px', height: '100%' }}>
        <StyledLoading
          data-test-id="loading"
          $dark={$dark}
          $isRelative={$isRelative}
          $isSmall={$isSmall}
          $isFixed={$isFixed}
          $hasNoOffset={$hasNoOffset}
        >
          <div />
          <div />
          <div />
          <div />
        </StyledLoading>
      </div>
    );
  }
  return (
    <StyledLoading
      data-test-id="loading"
      $dark={$dark}
      $isRelative={$isRelative}
      $isSmall={$isSmall}
      $isFixed={$isFixed}
      $hasNoOffset={$hasNoOffset}
    >
      <div />
      <div />
      <div />
      <div />
    </StyledLoading>
  );
}

export default Loading;
